/* eslint-disable @typescript-eslint/no-explicit-any */
import {enqueueSnackbar} from 'notistack';
import jobDetailService from '@jobseeker/common-core/services/jobdetail.service';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {getOldLink} from '@jobseeker/lotus/utils';
import {handleTracking, showErrorToast} from '@jobseeker/common-core/utils';
import {ROUTE_JOB_APPLIED} from '@constants';
import {checkIsTechDrive} from '@components/JobDetail/utils';
import {kebabCase} from 'lodash';

let Origin = '';
const isAppHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

const parseResumeUpdatedAt = (value?: string) => {
  if (!value) return null;
  // Expected format: "YYYY-MM-DD HH:mm:ss"
  const parsed = Date.parse(value.replace(' ', 'T'));
  return Number.isNaN(parsed) ? null : new Date(parsed);
};

export const shouldShowReviewSubmitForIIMJobs = (userData: any) => {
  if (isAppHirist) return false;
  const resumeDate = parseResumeUpdatedAt(userData?.profile?.resumeUpdatedAt);
  if (!resumeDate) return false;

  const cutoffDate = new Date();
  cutoffDate.setMonth(cutoffDate.getMonth() - 12);
  return resumeDate < cutoffDate;
};

export const handleJobApply = async (
  jobdetail: any,
  coverLetter: string | any,
  showSnackBar = true,
  pushToConfirmation = true,
  handleApplyLoaderForReAppliedUser = () => {},
  refInfo = 'jd',
  query: Record<string, string | string[] | any>
) => {
  try {
    const response = await jobDetailService.jobApply({
      jobId: jobdetail.id,
      coverLetter,
    });
    if (showSnackBar) {
      enqueueSnackbar((response as any).message ?? 'Job Applied Successfully', {
        variant: 'success',
      });
    }
    if (pushToConfirmation) {
      const params = buildQueryParams(query, {
        ref: refInfo,
        jobId: String(jobdetail.id),
      });
      openUrl(`${ROUTE_JOB_APPLIED}?${params}`, '_self');
      // router.push({pathname: '/job/applied', query: {jobId: jobdetail.id}});
    }
    return true;
  } catch (error: any) {
    if (error?.response?.data?.error?.name === 'JOB_ALREADY_APPLIED') {
      if (window) window.location.reload();
      return false;
    }
    if (
      showSnackBar ||
      error?.response?.status === 400 ||
      error?.message === 'Something went wrong. Please try again!'
    ) {
      showErrorToast(
        error.response?.data?.error?.message ??
          'Something went wrong. Please try again!',
        error
      );

      if (
        isAppHirist &&
        error?.response?.data?.error?.name === 'JOB_ALREADY_APPLIED'
      ) {
        handleApplyLoaderForReAppliedUser?.();
      }
    }
    return false;
  }
};

export const validateJobApplicationRedirectionsOld = (
  jobInfo: any,
  userData: any,
  refInfo = 'jd',
  jobPos: any
) => {
  const {
    profile: {audioPath, videoPath, experienceStatus},
    completeness: {profileStatus},
  } = userData;

  const isRoundZeroProfileExists = audioPath || videoPath;
  const enableJobAssessment =
    jobInfo?.assessmentFlags && !jobInfo?.assessmentCompleted ? true : false;
  const slug = kebabCase(jobInfo.title.replace(/&/g, '-and-'));
  const redirectPath = `j/${slug}-${jobInfo.id}?ref=${refInfo}`;
  if (enableJobAssessment) {
    const JOB_ASSESSMENT_URL: {[key: string]: string} = {
      '11': `/jobassessment/${jobInfo?.id}?ref=${refInfo}&inline=false&jobid=${jobInfo?.id}&version=2&rzcase=3&redirect_url=${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${redirectPath}`,
      '12': `/jobassessment/${jobInfo?.id}?ref=${refInfo}&inline=false&jobid=${jobInfo?.id}&version=2&rzcase=4&redirect_url=${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${redirectPath}`,
      '01': `/jobassessment/${jobInfo?.id}?ref=${refInfo}&inline=false&jobid=${jobInfo?.id}&version=2&rzcase=1&ref=rzmj&redirect_url=${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${redirectPath}`,
      '02': `/jobassessment/${jobInfo?.id}?ref=${refInfo}&inline=false&jobid=${jobInfo?.id}&version=2&rzcase=2&ref=rzpj&redirect_url=${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${redirectPath}`,
      '10': `/jobassessment/${jobInfo?.id}?ref=${refInfo}&inline=false&jobid=${jobInfo?.id}&version=2&redirect_url=${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${redirectPath}`,
    };

    const assessmentFlagsBinaryMask = jobInfo?.assessmentFlags.toString(2);
    const assessmentQuesting = assessmentFlagsBinaryMask?.slice(-1);

    const assessmentUrl =
      !isRoundZeroProfileExists && jobInfo?.mediaResume
        ? JOB_ASSESSMENT_URL[`${assessmentQuesting}${jobInfo?.mediaResume}`]
        : JOB_ASSESSMENT_URL['10'];
    console.log(assessmentUrl);
    const params = new URLSearchParams({ref: refInfo});
    if (jobPos) params.append('jobPos', jobPos);
    return `/job/${jobInfo.id}/screening?${params.toString()}`;
  }

  // Incomplete Profile Cases
  let profileEdit = '';
  let baseUrl = '';

  if (!profileStatus?.personalDetails) {
    profileEdit = `${profileEdit}&pd=pd`;
    baseUrl = `/registration/addPersonalDetails?profiletype=1${profileEdit}&has=1&redirectto=${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${redirectPath}`;
  }

  if (!profileStatus?.educationDetails && !baseUrl) {
    profileEdit = `${profileEdit}&ed=ed`;
    baseUrl = `/registration/addEducationalDetails?profiletype=1${profileEdit}&has=1&redirectto=${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${redirectPath}`;
  }

  if (!profileStatus?.resumeAttached && !baseUrl) {
    profileEdit = `${profileEdit}&ur=ur`;
    baseUrl = `/registration/addPersonalDetails?profiletype=1${profileEdit}&has=1&redirectto=${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${redirectPath}`;
  }

  if (
    !profileStatus?.professionalDetails &&
    !(experienceStatus === 2) &&
    !baseUrl
  ) {
    profileEdit = `${profileEdit}&pro=pro`;
    baseUrl = `/registration/addWorkExperience?profiletype=1${profileEdit}&has=1&redirectto=${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${redirectPath}`;
  }

  if (baseUrl) {
    return getOldLink(baseUrl);
  }

  // Round Zero Specific for no assessment jobs
  if (
    !isRoundZeroProfileExists &&
    jobInfo?.mediaResume &&
    !enableJobAssessment
  ) {
    const ROUNDZERO_PATH: {[key: string]: string} = {
      '1': `/registration/addAudioVideoProfile?jobid=${jobInfo?.id}&rzcase=1&ref=rzmj&redirect_url=${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${redirectPath}`,
      '2': `/registration/addAudioVideoProfile?jobid=${jobInfo?.id}&rzcase=2&ref=rzpj&redirect_url=${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${redirectPath}`,
    };
    if (jobInfo?.mediaResume == 2 && jobInfo.rzSkipped) {
      return;
    }
    console.log(ROUNDZERO_PATH);

    const params = new URLSearchParams({ref: refInfo});
    if (jobPos) params.append('jobPos', jobPos);
    return `/job/${jobInfo.id}/screening?${params.toString()}`;
    // return getOldLink(ROUNDZERO_PATH[`${jobInfo?.mediaResume}`]);
  }
};

export const applyOnJob = async (options: any) => {
  const {
    isLoggedIn,
    performLogoutAction,
    setLoginRedirect,
    setApplyJobDialogState,
    jobInfo,
    setOpenResumeBanner,
    router,
    userData,
    refInfo,
  } = options;
  const {
    profile: {email, coverLetterId, profileUpdateCohorts},
  } = userData;

  handleTracking('ClickApply', {
    event_category: 'Job',
    Origin: router.asPath,
    Source: 'JobDescription',
    CategoryId: jobInfo?.categoryId,
    JobId: jobInfo?.id,
    RecruiterId: jobInfo.recruiter?.recruiterId,
    UserId: sessionStorage.getItem('userId') || 'NA',
    Device: sessionStorage.getItem('device') || 'desktop',
    Device_Type: sessionStorage.getItem('deviceType') || 'web',
    Status: sessionStorage.getItem('status') || 'LoggedOut',
    Php_SessionID_Mod: sessionStorage.getItem('phpSessId') || '0',
    content_id: jobInfo?.id,
    content_type: 'product',
  });

  if (!isLoggedIn) {
    performLogoutAction && performLogoutAction();
    setLoginRedirect && setLoginRedirect('openApply');
    return false;
  }
  if (!email) {
    return false;
  }

  // if (!resumeUpdated) {
  //   setOpenResumeBanner && setOpenResumeBanner(true);
  //   return;
  // }
  if (
    profileUpdateCohorts &&
    Object.values(profileUpdateCohorts).some(value => value === true)
  ) {
    setOpenResumeBanner && setOpenResumeBanner(true);
    return false;
  }

  /**
   * Assessment Question and Round Zero based job assessment url mappings.
   */
  const {redirectUrl} = validateJobApplicationRedirections(
    jobInfo,
    userData,
    refInfo,
    router.query
  );
  if (redirectUrl) {
    openUrl(redirectUrl, '_self');
    return true;
  } else {
    // for cases when job application hardly forwarded to apply on company portal
    if (jobInfo?.applyUrl && !jobInfo?.confidential) {
      handleJobApply(
        jobInfo,
        coverLetterId,
        false,
        true,
        () => {},
        'jd',
        router.query
      );
      openUrl(jobInfo.applyUrl);
      return true;
    }

    if (shouldShowReviewSubmitForIIMJobs(userData)) {
      setApplyJobDialogState?.(true);
      return false;
    }
    const isApplied = await handleJobApply(
      jobInfo,
      coverLetterId,
      false,
      true,
      () => {},
      'jd',
      router.query
    );
    return !!isApplied;
  }
};

export const buildQueryParams = (
  query: Record<string, string | string[] | any>,
  overrides: Record<string, string> = {}
): string => {
  const params = new URLSearchParams();
  const excludedKeys = ['jobId', 'pref', 'jobslug'];

  Object.entries(query).forEach(([key, value]) => {
    if (excludedKeys.includes(key)) return;

    if (Array.isArray(value)) {
      value.forEach(v => v && params.append(key, v));
    } else if (value) {
      params.set(key, value);
    }
  });

  Object.entries(overrides).forEach(([key, value]) => {
    if (value && !params.has(key)) params.set(key, value);
  });
  return params.toString();
};

export const validateJobApplicationRedirections = (
  jobInfo: any,
  userData: any,
  refInfo = 'jd',
  query: Record<string, string | string[] | any> = {}
) => {
  const {
    profile: {audioPath, videoPath, experienceStatus, resumeType},
    completeness: {profileStatus},
  } = userData;

  const isRoundZeroProfileExists = audioPath || videoPath;
  const enableJobAssessment =
    jobInfo?.assessmentFlags && !jobInfo?.assessmentCompleted ? true : false;
  const slug = kebabCase(jobInfo.title.replace(/&/g, '-and-'));
  const redirectQuery = buildQueryParams(query, {ref: refInfo});

  const redirectPath = isAppHirist
    ? `j/${slug}-${jobInfo.id}?${redirectQuery}`
    : shouldShowReviewSubmitForIIMJobs(userData)
    ? `job/${jobInfo.id}/review-apply?${redirectQuery}`
    : `j/${slug}-${jobInfo.id}?${redirectQuery}`;
  const encodedRedirectUrl = encodeURIComponent(
    `${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${redirectPath}`
  );

  // Incomplete Profile Cases
  let profileEdit = '';
  let baseUrl = '';

  if (resumeType !== 2 && !profileStatus?.resumeAttached) {
    profileEdit = `${profileEdit}&ur=ur`;
    baseUrl = `/registration/addPersonalDetails?profiletype=1${profileEdit}&has=1&redirectto=${encodedRedirectUrl}`;
  }

  if (!profileStatus?.personalDetails) {
    profileEdit = `${profileEdit}&pd=pd`;
    baseUrl = `/registration/addPersonalDetails?profiletype=1${profileEdit}&has=1&redirectto=${encodedRedirectUrl}`;
  }

  if (!profileStatus?.educationDetails && !baseUrl) {
    profileEdit = `${profileEdit}&ed=ed`;
    baseUrl = `/registration/addEducationalDetails?profiletype=1${profileEdit}&has=1&redirectto=${encodedRedirectUrl}`;

    if (isAppHirist) {
      // Most relevant education is not present
      handleTracking('MostRelevantRedirect', {
        event_category: 'Job',
        Origin,
        Source: 'JobDescription',
        CategoryId: jobInfo?.categoryId,
        JobId: jobInfo?.id,
        RecruiterId: jobInfo.recruiter?.recruiterId,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
        Ref: refInfo,
      });
    }
  }

  if (
    !profileStatus?.professionalDetails &&
    !(experienceStatus === 2) &&
    !baseUrl
  ) {
    profileEdit = `${profileEdit}&pro=pro`;
    baseUrl = `/registration/addWorkExperience?profiletype=1${profileEdit}&redirectto=${encodedRedirectUrl}`;
  }

  if (resumeType === 2 && !profileStatus?.resumeAttached && !baseUrl) {
    return {redirectUrl: '', resumeAutoGenerate: true};
  }

  if (baseUrl) {
    // NOTE: The following code redirects the user to the old PHP onboarding page
    // if (platform === 'hirist') {
    //   baseUrl = `/registration/registration.php?profiletype=1${profileEdit}&redirectto=${encodeURIComponent(
    //     window.location.href
    //   )}`;
    // }
    if (isAppHirist) {
      handleTracking('RegistrationRedirect', {
        event_category: 'Job',
        Origin,
        Source: 'JobDescription',
        CategoryId: jobInfo?.categoryId,
        JobId: jobInfo?.id,
        RecruiterId: jobInfo.recruiter?.recruiterId,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
        Ref: refInfo,
      });
    }
    return {redirectUrl: getOldLink(baseUrl), resumeAutoGenerate: false};
  }

  if (enableJobAssessment) {
    if (isAppHirist) {
      handleTracking('ScreeningRedirect', {
        event_category: 'Job',
        Origin,
        Source: 'JobDescription',
        CategoryId: jobInfo?.categoryId,
        JobId: jobInfo?.id,
        RecruiterId: jobInfo.recruiter?.recruiterId,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
        Ref: refInfo,
      });
    }
    return {
      redirectUrl: `/job/${jobInfo.id}/screening?${redirectQuery}`,
      resumeAutoGenerate: false,
    };
  }

  if (
    !isRoundZeroProfileExists &&
    jobInfo?.mediaResume &&
    !enableJobAssessment
  ) {
    if (jobInfo?.mediaResume == 2 && jobInfo.rzSkipped) {
      return {redirectUrl: '', resumeAutoGenerate: false};
    }

    if (isAppHirist) {
      handleTracking('jsClickRecord', {
        event_category: 'Round Zero Mandatory Job',
        Origin,
        Source: 'JobDescription',
        CategoryId: jobInfo?.categoryId,
        JobId: jobInfo?.id,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Ref: refInfo,
      });
    }
    return {
      redirectUrl: `/job/${jobInfo.id}/screening?${redirectQuery}`,
      resumeAutoGenerate: false,
    };
  }
  return {redirectUrl: '', resumeAutoGenerate: false};
};

export const handleJobApplicationOnHirist = (options?: any) => {
  const {
    isLoggedIn,
    performLogoutAction,
    jobInfo,
    userData,
    refInfo,
    router,
    setOpenResumeBanner,
    handleApplyLoaderForReAppliedUser,
    setIsProfileUpdateModalOpen,
    setIsResumeAutoGeneratedCompleted,
  } = options;
  const {
    profile: {email, coverLetterId, profileUpdateCohorts, functionalArea},
  } = userData;

  Origin = router.asPath;

  handleTracking('ClickApply', {
    event_category: 'Job',
    Origin,
    Source: 'JobDescription',
    CategoryId: jobInfo?.categoryId,
    JobId: jobInfo?.id,
    RecruiterId: jobInfo.recruiter?.recruiterId,
    UserId: sessionStorage.getItem('userId') || 'NA',
    Device: sessionStorage.getItem('device') || 'desktop',
    Device_Type: sessionStorage.getItem('deviceType') || 'web',
    Status: sessionStorage.getItem('status') || 'LoggedOut',
    Php_SessionID_Mod: sessionStorage.getItem('phpSessId') || '0',
    content_id: jobInfo?.id,
    content_type: 'product',
  });

  const isTechDrive = checkIsTechDrive();

  if (isTechDrive) {
    handleTracking('JDApplyClick', {
      event_category: 'Offline Landing page',
      click: 1,
    });
  }

  if (!isLoggedIn) {
    performLogoutAction && performLogoutAction();
    return;
  }
  if (!email) {
    return;
  }

  if (
    profileUpdateCohorts &&
    Object.values(profileUpdateCohorts).some(value => value === true)
  ) {
    setOpenResumeBanner && setOpenResumeBanner(true);
    return;
  }
  if (functionalArea?.id == 6 || functionalArea?.id == 1) {
    const lastShownTimestamp = localStorage.getItem(
      'functionalAreaModalLastShown'
    );
    const currentTime = new Date().getTime();
    const oneDayInMs = 24 * 60 * 60 * 1000;

    let shouldShowModal = false;

    if (!lastShownTimestamp) {
      shouldShowModal = true;
      localStorage.setItem(
        'functionalAreaModalLastShown',
        currentTime.toString()
      );
    } else {
      const timeDifference = currentTime - parseInt(lastShownTimestamp, 10);
      if (timeDifference >= oneDayInMs) {
        shouldShowModal = true;
        localStorage.setItem(
          'functionalAreaModalLastShown',
          currentTime.toString()
        );
      }
    }

    if (shouldShowModal) {
      setIsProfileUpdateModalOpen && setIsProfileUpdateModalOpen(true);
      return;
    }
  }
  const {redirectUrl, resumeAutoGenerate} = validateJobApplicationRedirections(
    jobInfo,
    userData,
    refInfo,
    router.query
  );

  if (resumeAutoGenerate) {
    setIsResumeAutoGeneratedCompleted &&
      setIsResumeAutoGeneratedCompleted(true);
    return;
  }
  if (redirectUrl) {
    openUrl(redirectUrl, '_self');
    return;
  }

  // for cases when job application hardly forwarded to apply on company portal
  if (jobInfo?.applyUrl && !jobInfo?.confidential) {
    handleJobApply(
      jobInfo,
      coverLetterId,
      false,
      true,
      handleApplyLoaderForReAppliedUser,
      'jd',
      router.query
    );
    openUrl(jobInfo.applyUrl);
    return;
  }

  handleJobApply(
    jobInfo,
    coverLetterId,
    true,
    true,
    handleApplyLoaderForReAppliedUser,
    refInfo,
    router.query
  );
};
